import {
  BuilderAdditionalProps,
  BuilderAttributes,
} from "@faire/builder-core/registerBuilderComponents/__internal__/BuilderTypes";
import { ImpressionProviderProps } from "@faire/deprecated-retailer-visitor-shared/components/LeanBrandTileRenderer/__internal__/ImpressionProviderProps";
import { BrandImpressionType } from "@faire/deprecated-retailer-visitor-shared/components/LeanBrandTileRenderer/BrandImpressionType";
import {
  BrandTile,
  IBrandTileProps,
} from "@faire/deprecated-retailer-visitor-shared/components/LeanBrandTileRenderer/BrandTile";
import { ILeanBrandTileAdapter } from "@faire/deprecated-retailer-visitor-shared/components/LeanBrandTileRenderer/ILeanBrandTileAdapters";
import { SignUpElement } from "@faire/deprecated-retailer-visitor-shared/lib/popSignUp";
import { useQuery_escape_hatch as useBulkFetchBrandsQuery } from "@faire/web-api--source/endpoints/www/api/v3/layout/bulk-brand-tiles/post-hooks";
import { BrandTileSurfaceContainer } from "@faire/web-api--source/indigofair/data/BrandTileSurfaceContainer";
import { Grid, IGridProps } from "@faire/web/slate/Layout/Grid";
import React from "react";

import {
  deduplicateProductsOrBrands,
  MOCK_BRAND_TILE,
  shouldRenderProductionTile,
} from "./utils";

export interface IBrandInput
  extends Pick<
    IBrandTileProps,
    "type" | "withoutFavourite" | "showMinimum" | "impressionProviderProps"
  > {
  tokenStaging: string;
  tokenProd: string;
}

type BrandTileImpressionProviderProps = Pick<
  Extract<ImpressionProviderProps, { type?: BrandImpressionType.TILE }>,
  "contextToken" | "surfaceContext" | "containerContext"
>;

interface IProps
  extends Omit<IGridProps, "children" | "columnTemplate">,
    BuilderAdditionalProps {
  brands: IBrandInput[];
  useCustomStagingToken: boolean;
  loggingContext: BrandTileImpressionProviderProps;
  attributes: BuilderAttributes;
}

export const BuilderRetailerBrandTileSection: React.FC<IProps> = ({
  brands: brandInputs,
  useCustomStagingToken,
  loggingContext,
  attributes,
  builderBlock,
  builderState,
  ...gridProps
}) => {
  const renderMockTile =
    !useCustomStagingToken && !shouldRenderProductionTile();

  const visibleBrandInputs = renderMockTile
    ? brandInputs
    : deduplicateProductsOrBrands(brandInputs, !shouldRenderProductionTile());

  const { data: liveBrandTileData, isPending } = useBulkFetchBrandsQuery(
    {
      brand_tokens: visibleBrandInputs.map((brand) =>
        !shouldRenderProductionTile() ? brand.tokenStaging : brand.tokenProd
      ),
      product_surface_container: undefined,
      product_container_variant: undefined,
    },
    { enabled: !renderMockTile }
  );

  if (!renderMockTile && (isPending || !liveBrandTileData)) {
    return null;
  }

  const { brand_tiles } = liveBrandTileData ?? {};

  const brandTiles =
    (renderMockTile
      ? Array(visibleBrandInputs.length).fill(MOCK_BRAND_TILE)
      : brand_tiles) ?? [];

  return (
    <Grid
      {...gridProps}
      className={`w-full ${attributes.className}`}
      key={attributes["key"]}
      style={attributes.style}
    >
      {brandTiles?.map((brandTile, index) => (
        <BrandTile
          key={`${brandTile.token}-${index}`}
          signUpTracking={{
            element: SignUpElement.PRODUCT_GRID,
            sourceId: "builder-io-retailer-brand-tile",
          }}
          type={visibleBrandInputs[index]?.type ?? "common"}
          brandTile={ILeanBrandTileAdapter.fromIBrandTile(brandTile)}
          withoutFavourite={visibleBrandInputs[index]?.withoutFavourite}
          showMinimum={visibleBrandInputs[index]?.showMinimum}
          impressionProviderProps={{
            ...loggingContext,
            containerContext:
              loggingContext.containerContext ??
              BrandTileSurfaceContainer.BUILDER_IO_BRANDS,
            type: BrandImpressionType.TILE,
            requestId: brandTile.request_id,
          }}
        />
      ))}
    </Grid>
  );
};
